import { Box, Stack } from '@mui/material';
import { useAppSelector, useDictionary, useIsDesktop } from 'Hooks';
import { useBankDetailsForm } from 'Hooks/useBankDetailsForm';
import { FormProvider } from 'react-hook-form';
import { Form } from './Form';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { RequestsContext } from 'Context';
import { IUpdateBankDetails } from 'Interfaces/updateBankDetails';
import { FormActionButtons } from 'Modules/Requests/FormActionButtons';
import { useNavigate } from 'react-router-dom';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';

interface IBankDetailsForm {
  nextStep: () => void;
}

export const BankDetailsForm = ({ nextStep }: IBankDetailsForm) => {
  const { setRequestsData } = useContext(RequestsContext);

  const [errorMessage, setErrorMessage] = useState<string>('');

  const isDesktop = useIsDesktop();
  const navigate = useNavigate();
  const dictionary = useDictionary(Dictionary, DICTIONARY.UpdateBankDetails);

  const personalDetails = useAppSelector(
    ({ personalDetails: { personalDetails } }) => personalDetails
  );

  const formRef = useRef<HTMLFormElement>(null);

  const { bankDetailsForm, setBankNumber, setBranchNumber } =
    useBankDetailsForm();

  useEffect(() => {
    setBankNumber(bankDetailsForm.watch('bankId'));
  }, [bankDetailsForm.watch('bankId'), setBankNumber]);

  useEffect(() => {
    setBranchNumber(bankDetailsForm.watch('branchId'));
  }, [bankDetailsForm.watch('branchId'), setBranchNumber]);

  const areDetailsUnchanged = useCallback(
    () =>
      personalDetails?.bankDetails?.bankCode ===
        `${bankDetailsForm.watch('bankId')}` &&
      `${personalDetails?.bankDetails?.bankCode}${personalDetails?.bankDetails?.branchCode}` ===
        `${bankDetailsForm.watch('branchId')}` &&
      personalDetails?.bankDetails?.accountNum ===
        bankDetailsForm.watch('accountNum'),
    [
      bankDetailsForm,
      personalDetails?.bankDetails?.accountNum,
      personalDetails?.bankDetails?.bankCode,
      personalDetails?.bankDetails?.branchCode,
      bankDetailsForm.watch('accountNum'),
      bankDetailsForm.watch('bankId'),
      bankDetailsForm.watch('branchId'),
    ]
  );

  const onSubmit = async (
    e:
      | React.MouseEvent<HTMLButtonElement, MouseEvent>
      | React.FormEvent<HTMLFormElement>
  ) => {
    e.preventDefault();

    if (areDetailsUnchanged()) {
      setErrorMessage(dictionary.errorMessage);
      return;
    }

    let requestDataJson: IUpdateBankDetails | null = null;
    await bankDetailsForm.handleSubmit((data) => {
      requestDataJson = { ...data };
    })();

    if (Object.keys(bankDetailsForm.formState.errors).length === 0) {
      setRequestsData((prev) => {
        return {
          ...prev,
          requestDataJson,
        };
      });

      nextStep();
    }
  };

  useEffect(() => {
    if (errorMessage && !areDetailsUnchanged()) setErrorMessage('');
  }, [areDetailsUnchanged, errorMessage]);

  useEffect(() => {
    if (personalDetails?.bankDetails?.bankCode) {
      bankDetailsForm.setValue(
        'bankId',
        parseInt(personalDetails.bankDetails.bankCode)
      );

      if (personalDetails?.bankDetails?.branchCode) {
        setTimeout(() => {
          bankDetailsForm.setValue(
            'branchId',
            parseInt(
              `${personalDetails.bankDetails.bankCode}${personalDetails.bankDetails.branchCode}`
            )
          );
        }, 0);
      }
    }

    if (personalDetails?.bankDetails?.accountNum)
      bankDetailsForm.setValue(
        'accountNum',
        personalDetails.bankDetails.accountNum
      );
  }, [bankDetailsForm, personalDetails]);

  return (
    <>
      <form ref={formRef} onSubmit={onSubmit}>
        <Stack alignItems={'center'}>
          <Stack maxWidth={isDesktop ? '400px' : 'initial'} gap={'24px'}>
            <FormProvider {...bankDetailsForm}>
              <Form />
            </FormProvider>
          </Stack>
        </Stack>
      </form>
      <Box
        sx={{
          '.MuiFormHelperText-root': {
            fontSize: '20px',
            lineHeight: '24px',
            mb: '24px',
          },
        }}
      >
        <FormActionButtons
          next={onSubmit}
          previous={() => navigate('/universalRequest')}
          errorMessage={errorMessage}
        />
      </Box>
    </>
  );
};
